import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { ContractCumulative } from './contract-cumulative.model';
import * as ContractCumulativeActions from './contract-cumulative.actions';
import { CalcType } from '../../lookups';
import { Users } from './users.model';
import { SessionDates } from '../SessionDates';

export const contractCumulativesFeatureKey = 'contractCumulatives';

export interface State extends EntityState<ContractCumulative> {
  // additional entities state properties
  txnTypes: CalcType[];
  users: Users[];
  sessionDates: SessionDates[];
}

export const adapter: EntityAdapter<ContractCumulative> = createEntityAdapter<ContractCumulative>({selectId: (model: ContractCumulative) => model.Txnid});

export const initialState: State = adapter.getInitialState({
  // additional entity state properties
  txnTypes: [],
  users: [],
  sessionDates: []
});


export const contractCumulativeReducer = createReducer(
  initialState,
  on(ContractCumulativeActions.addContractCumulative,
    (state, action) => adapter.addOne(action.contractCumulative, state)
  ),
  on(ContractCumulativeActions.upsertContractCumulative,
    (state, action) => adapter.upsertOne(action.contractCumulative, state)
  ),
  on(ContractCumulativeActions.addContractCumulatives,
    (state, action) => adapter.addMany(action.contractCumulatives, state)
  ),
  on(ContractCumulativeActions.upsertContractCumulatives,
    (state, action) => adapter.upsertMany(action.contractCumulatives, state)
  ),
  on(ContractCumulativeActions.updateContractCumulative,
    (state, action) => adapter.updateOne(action.contractCumulative, state)
  ),
  on(ContractCumulativeActions.updateContractCumulatives,
    (state, action) => adapter.updateMany(action.contractCumulatives, state)
  ),
  on(ContractCumulativeActions.deleteContractCumulative,
    (state, action) => adapter.removeOne(action.id, state)
  ),
  on(ContractCumulativeActions.deleteContractCumulatives,
    (state, action) => adapter.removeMany(action.ids, state)
  ),
  on(ContractCumulativeActions.loadContractCumulatives,
    (state, action) => adapter.setAll(action.contractCumulatives, state)
  ),
  on(ContractCumulativeActions.clearContractCumulatives,
    state => adapter.removeAll(state)
  ),
  on(ContractCumulativeActions.loadTxnTypes, (state, action) => ({
    ...state,
    txnTypes: action.txnTypes
  })),
  on(ContractCumulativeActions.loadUsers, (state, action) => ({
    ...state,
    users: action.users
  })),
  on(ContractCumulativeActions.loadSessionDates, (state, action) => ({
    ...state,
    sessionDates: action.sessionDates
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return contractCumulativeReducer(state, action);
}

export const selectContractCumulativeState = createFeatureSelector<State>(
  contractCumulativesFeatureKey
);

export const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = adapter.getSelectors(selectContractCumulativeState);

export const selectTxnTypes = createSelector(
  selectContractCumulativeState,
  x => x.txnTypes
);

export const selectUsers = createSelector(
  selectContractCumulativeState,
  x => x.users
);

export const selectSessionDates = createSelector(
  selectContractCumulativeState,
  x => x.sessionDates
);
